import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Mail } from "lucide-react";

export default function ContactPage() {
  const API = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: "",
    error: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: "", error: "" });

    try {
      const response = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to submit inquiry");
      }

      setFormStatus({
        loading: false,
        success: "Thank you. Your inquiry has been submitted successfully.",
        error: "",
      });

      setFormData({
        name: "",
        company: "",
        email: "",
        message: "",
      });
    } catch (error) {
      setFormStatus({
        loading: false,
        success: "",
        error: error.message || "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div className="py-20">
      <div className="mx-auto max-w-5xl px-6">

        {/* Contact Info Strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 grid gap-4 sm:grid-cols-3"
        >
          <a
            href="tel:+918697702088"
            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur transition hover:border-cyan-400/40"
          >
            <Phone size={20} className="shrink-0 text-cyan-300" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Call Us</p>
              <p className="mt-0.5 font-semibold">+91 8697702088</p>
            </div>
          </a>

          <a
            href="mailto:info@hydroteclimited.com"
            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur transition hover:border-cyan-400/40"
          >
            <Mail size={20} className="shrink-0 text-cyan-300" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Email Us</p>
              <p className="mt-0.5 font-semibold">info@hydroteclimited.com</p>
            </div>
          </a>

          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur">
            <MapPin size={20} className="shrink-0 text-cyan-300" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Address</p>
              <p className="mt-0.5 text-sm leading-5">CC-100, Street-233, Newtown, Kolkata, West Bengal - 700156</p>
            </div>
          </div>
        </motion.div>

        {/* Area Coverage */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-10 rounded-2xl border border-cyan-400/15 bg-cyan-500/5 px-6 py-4 text-sm text-slate-300"
        >
          <span className="font-semibold text-cyan-300">Serving: </span>
          Newtown · Rajarhat · Salt Lake · Kolkata North · Kolkata South · Greater Kolkata Region
          — arsenic removal plants, iron removal plants, softener plants, RO plants, UV systems
          and water filters for residential societies, gated communities, villas and luxury developments.
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="rounded-[36px] border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-2xl md:p-12"
        >
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
              Consultation
            </p>
            <h3 className="mt-4 text-4xl font-bold md:text-5xl">
              Plan the Right Water Treatment Plant for Your Property
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="mt-10">
            <div className="grid gap-4 md:grid-cols-2">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Name"
                required
                className="rounded-2xl border border-white/10 bg-slate-900/70 px-5 py-4 text-white outline-none focus:border-cyan-400"
              />
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Company / Society Name"
                required
                className="rounded-2xl border border-white/10 bg-slate-900/70 px-5 py-4 text-white outline-none focus:border-cyan-400"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email Address"
                required
                className="rounded-2xl border border-white/10 bg-slate-900/70 px-5 py-4 text-white outline-none focus:border-cyan-400 md:col-span-2"
              />
              <textarea
                rows="5"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your property and water requirement"
                required
                className="rounded-2xl border border-white/10 bg-slate-900/70 px-5 py-4 text-white outline-none focus:border-cyan-400 md:col-span-2"
              />
            </div>

            {formStatus.success && (
              <div className="mt-4 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
                {formStatus.success}
              </div>
            )}

            {formStatus.error && (
              <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {formStatus.error}
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                type="submit"
                disabled={formStatus.loading}
                className="rounded-2xl bg-cyan-500 px-8 py-4 font-bold text-slate-950 transition shadow-lg shadow-cyan-500/20 hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {formStatus.loading ? "Submitting..." : "Submit Inquiry"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}